import React, { useMemo, useState } from 'react';
import Card from './Card';
import KpiCard from './KpiCard';
import { inputStyle } from './FormField';
import * as dispatchApi from '../services/dispatchAnalysisApi';
import { formatDate } from '../utils/dateFormat';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const PAGE_SIZE = 25;
const STATUSES = ['On track', 'Due soon', 'Overdue', 'At risk', 'New', 'Inactive'];
const STATUS_STYLES = {
  'On track': { bg: '#dcfce7', fg: '#166534' },
  'Due soon': { bg: '#dbeafe', fg: '#1e40af' },
  Overdue: { bg: '#fef3c7', fg: '#92400e' },
  'At risk': { bg: '#fee2e2', fg: '#991b1b' },
  New: { bg: '#ede9fe', fg: '#5b21b6' },
  Inactive: { bg: '#e5e7eb', fg: '#374151' },
};
const CONFIDENCE_COLORS = { High: '#166534', Medium: '#92400e', Low: '#6b7280' };
const SORTS = [
  { key: 'qty6', label: 'Last 6M qty (high to low)' },
  { key: 'next', label: 'Next expected date (soonest)' },
  { key: 'pred', label: 'Predicted 3M qty (high to low)' },
  { key: 'since', label: 'Days since last purchase (most)' },
  { key: 'name', label: 'Customer A to Z' },
];

function fmtDate(d) {
  return formatDate(d);
}

function fmtShort(s) {
  const [, m, d] = s.split('-');
  return `${d} ${MONTHS[Number(m) - 1]}`;
}

const thStyle = {
  padding: '8px 6px', fontSize: 'var(--font-size-xs)', fontWeight: 700, color: '#1e293b',
  background: '#e2e8f0', textAlign: 'left', borderBottom: '2px solid #94a3b8', borderRight: '1px solid #cbd5e1', whiteSpace: 'nowrap',
};
const tdStyle = { padding: '8px 8px', fontSize: 'var(--font-size-sm)', verticalAlign: 'top', borderBottom: '1px solid var(--color-border)', borderRight: '1px solid var(--color-border)' };

function StatusPill({ status }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.Inactive;
  return (
    <span style={{ display: 'inline-block', fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 999, background: s.bg, color: s.fg, whiteSpace: 'nowrap' }}>
      {status}
    </span>
  );
}

function ItemLines({ items, max }) {
  const shown = max ? items.slice(0, max) : items;
  return (
    <div style={{ marginTop: 2 }}>
      {shown.map((it, i) => (
        <div key={`${it.size}-${it.design}-${i}`} style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', lineHeight: 1.45 }}>
          {it.design ? `${it.size} · ${it.design}` : it.size}
          {' — '}
          <span className="data-figure" style={{ color: 'var(--color-text-primary)', fontWeight: 600 }}>{it.qty}</span>
        </div>
      ))}
      {max && items.length > max && (
        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>+{items.length - max} more</div>
      )}
    </div>
  );
}

function PurchaseBlock({ p, color, maxItems }) {
  return (
    <div style={{ marginBottom: 8, paddingLeft: 8, borderLeft: `2px solid ${color}` }}>
      <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600 }}>
        {fmtDate(p.date)}
        <span className="data-figure" style={{ marginLeft: 8, color }}>{p.totalQty}</span>
      </div>
      <ItemLines items={p.items} max={maxItems} />
    </div>
  );
}

function csvCell(v) {
  const s = v === null || v === undefined ? '' : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function exportCsv(data, rows) {
  const lines = [[
    'No', 'Customer', 'Ref Party', 'City', 'State', 'Status', 'Confidence', 'Last purchase', 'Days since last',
    'Typical gap (days)', 'Qty last 6M', 'Qty previous 6M', 'Trend %', 'Next expected', 'Predicted orders (3M)',
    'Predicted qty (3M)', 'Purchases last 6M', 'Predicted purchases',
  ]];
  rows.forEach((r, i) => {
    const fmtP = (p) => `${fmtDate(p.date)}: ${p.items.map((it) => `${it.size} ${it.design} ${it.qty}`.replace(/\s+/g, ' ').trim()).join(' + ')} (total ${p.totalQty})`;
    lines.push([
      i + 1, r.customerName, r.refParty, r.city, r.state, r.status, r.confidence, r.lastPurchaseDate, r.daysSinceLast,
      r.typicalGapDays, r.qtyLast6m, r.qtyPrev6m, r.trendPct, r.nextExpectedDate, r.predictedOrderCount, r.predictedQty3m,
      r.history.map(fmtP).join(' | '), r.predictions.map(fmtP).join(' | '),
    ]);
  });
  const csv = `﻿${lines.map((l) => l.map(csvCell).join(',')).join('\r\n')}`;
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = `customer-tracking-${data.asOf}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function CustomerSearchBox({ value, onChange, rows, onPick, scoped }) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [rect, setRect] = useState(null);
  const ref = React.useRef(null);

  const q = value.trim().toLowerCase();
  const matches = useMemo(() => {
    if (!q) return scoped ? rows.slice().sort((a, b) => b.qtyLast6m - a.qtyLast6m).slice(0, 10) : [];
    const hit = rows.filter((r) => r.customerName.toLowerCase().includes(q));
    hit.sort((a, b) => {
      const sa = a.customerName.toLowerCase().startsWith(q) ? 0 : 1;
      const sb = b.customerName.toLowerCase().startsWith(q) ? 0 : 1;
      return sa - sb || a.customerName.localeCompare(b.customerName);
    });
    return hit.slice(0, 10);
  }, [rows, q, scoped]);

  const place = () => { if (ref.current) setRect(ref.current.getBoundingClientRect()); };
  React.useEffect(() => {
    if (!open) return undefined;
    const close = () => setOpen(false);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => { window.removeEventListener('scroll', close, true); window.removeEventListener('resize', close); };
  }, [open]);

  const pick = (r) => { setOpen(false); onPick(r); };
  const showList = open && rect && matches.length > 0;

  return (
    <>
      <input
        ref={ref}
        type="search"
        placeholder={scoped ? 'Search in this party…' : 'Search customer…'}
        aria-label="Search customers"
        autoComplete="off"
        value={value}
        onChange={(e) => { onChange(e.target.value); setActive(0); place(); setOpen(true); }}
        onFocus={() => { place(); setOpen(true); }}
        onBlur={() => setOpen(false)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, matches.length - 1)); }
          else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
          else if (e.key === 'Enter' && showList) { e.preventDefault(); pick(matches[active] || matches[0]); }
          else if (e.key === 'Escape') setOpen(false);
        }}
        style={{ ...inputStyle, width: '100%', marginTop: 4, fontWeight: 400 }}
      />
      {showList && (
        <ul
          role="listbox"
          style={{
            position: 'fixed', top: rect.bottom + 2, left: rect.left, width: Math.max(rect.width, 300), zIndex: 1000,
            margin: 0, padding: 0, listStyle: 'none', maxHeight: 320, overflowY: 'auto',
            background: 'var(--color-surface, #fff)', border: '1px solid var(--color-border)', borderRadius: 6,
            boxShadow: '0 8px 24px rgba(0,0,0,0.18)', fontWeight: 400,
          }}
        >
          {matches.map((r, i) => (
            <li
              key={r.customerId}
              role="option"
              aria-selected={i === active}
              onMouseDown={(e) => { e.preventDefault(); pick(r); }}
              onMouseEnter={() => setActive(i)}
              style={{ padding: '7px 10px', cursor: 'pointer', fontSize: 'var(--font-size-sm)', background: i === active ? 'var(--color-surface-alt, #eef2f7)' : 'transparent', whiteSpace: 'normal' }}
            >
              <div style={{ fontWeight: 600 }}>{r.customerName}</div>
              <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                {[r.refParty, r.city].filter(Boolean).join(' · ') || ' '}
              </div>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

export default function CustomerTrackingCard() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [refFilter, setRefFilter] = useState('');
  const [sortKey, setSortKey] = useState('qty6');
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [expanded, setExpanded] = useState({});
  const [pinnedId, setPinnedId] = useState(null);

  const load = () => {
    setLoading(true);
    setError(null);
    dispatchApi.getCustomerTracking()
      .then(setData)
      .catch((err) => setError(err.message || 'Could not load customer tracking.'))
      .finally(() => setLoading(false));
  };

  const toggleOpen = () => {
    const next = !open;
    setOpen(next);
    if (next && !data && !loading) load();
  };

  const refParties = useMemo(() => {
    if (!data) return [];
    return Array.from(new Set(data.rows.map((r) => r.refParty).filter(Boolean))).sort((a, b) => a.localeCompare(b));
  }, [data]);

  const filtered = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    const rows = data.rows.filter((r) => (
      (!statusFilter || r.status === statusFilter)
      && (!refFilter || r.refParty === refFilter)
      && (!q || [r.customerName, r.refParty, r.city, r.state].some((v) => v && v.toLowerCase().includes(q)))
    ));
    const cmp = {
      qty6: (a, b) => b.qtyLast6m - a.qtyLast6m,
      next: (a, b) => (a.nextExpectedDate || '9999').localeCompare(b.nextExpectedDate || '9999'),
      pred: (a, b) => b.predictedQty3m - a.predictedQty3m,
      since: (a, b) => b.daysSinceLast - a.daysSinceLast,
      name: (a, b) => a.customerName.localeCompare(b.customerName),
    }[sortKey];
    const sorted = rows.slice().sort(cmp);
    const at = pinnedId === null ? -1 : sorted.findIndex((r) => r.customerId === pinnedId);
    if (at > 0) sorted.unshift(sorted.splice(at, 1)[0]);
    return sorted;
  }, [data, search, statusFilter, refFilter, sortKey, pinnedId]);

  const autoOpen = search.trim() !== '' && filtered.length > 0 && filtered.length <= 3;
  const shown = filtered.slice(0, visible);
  const s = data ? data.summary : null;
  const needAttention = s ? (s.byStatus.Overdue || 0) + (s.byStatus['At risk'] || 0) : 0;
  const exp30Text = (() => {
    if (!data) return '';
    const lim = new Date(`${data.asOf}T00:00:00Z`);
    lim.setUTCDate(lim.getUTCDate() + 30);
    const limStr = lim.toISOString().slice(0, 10);
    const by = {};
    data.rows.forEach((r) => { if (r.nextExpectedDate && r.nextExpectedDate <= limStr) by[r.status] = (by[r.status] || 0) + 1; });
    return STATUSES.filter((k) => by[k]).map((k) => `${k} ${by[k]}`).join(' + ');
  })();

  return (
    <Card
      title="Customer Tracking"
      actions={(
        <button
          type="button"
          onClick={toggleOpen}
          style={{ ...inputStyle, width: 'auto', cursor: 'pointer', fontWeight: 600, padding: '6px 14px' }}
        >
          {open ? 'Hide' : 'Open'}
        </button>
      )}
    >
      {!open && (
        <p style={{ margin: 0, color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)' }}>
          Each customer&apos;s last 6 months of purchases and a forecast of their next 3 months (when, which size and design, how much).
        </p>
      )}

      {open && loading && <p style={{ color: 'var(--color-text-secondary)' }}>Loading customer history…</p>}
      {open && error && <p style={{ color: 'var(--color-danger)' }}>{error}</p>}

      {open && data && (
        <>
          <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap', marginBottom: 'var(--space-4)' }}>
            <KpiCard label="Customers tracked (bought in last 12 months)" value={s.customers} />
            <KpiCard label="Customers expected to order in next 30 days" value={s.expectedNext30Days} tone="success" />
            <KpiCard label="Overdue + at risk customers (follow-up needed)" value={needAttention} tone={needAttention > 0 ? 'warning' : 'success'} />
            <KpiCard label="Predicted qty, next 3 months (customers with a forecast)" value={s.predictedQty3m.toLocaleString()} />
            <KpiCard label="Predicted qty incl. new / one-time buyers" value={(s.predictedQty3m + (s.newBuyersQty3m || 0)).toLocaleString()} />
            <KpiCard label="Actual qty, last 6 months" value={s.qtyLast6m.toLocaleString()} />
          </div>
          <p style={{ margin: '0 0 var(--space-3)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', lineHeight: 1.6 }}>
            <strong>Tracked</strong>: customers who bought in the last 12 months (includes New and Inactive).{' '}
            <strong>Expected in next 30 days</strong>: customers whose next order is predicted within 30 days; overdue customers who may order any day are included{exp30Text ? ` (${exp30Text})` : ''}.{' '}
            <strong>Overdue</strong>: usual gap has passed. <strong>At risk</strong>: overdue by more than twice the usual gap (30+ days), not forecast.{' '}
            <strong>Predicted qty</strong>: sum of each forecast customer&apos;s run-rate (their last 6 months qty ÷ 2). At risk, New (one purchase) and Inactive customers are not included, so real totals can be higher. The "incl. new / one-time buyers" figure adds first-time customers at their own last-6-month pace (qty ÷ 2).
          </p>

          <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap', alignItems: 'center', marginBottom: 'var(--space-3)' }}>
            {search.trim() !== '' && (
              <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                {filtered.length} of {data.rows.length} customers
              </span>
            )}
            <select aria-label="Filter by status" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setVisible(PAGE_SIZE); }} style={{ ...inputStyle, width: 'auto' }}>
              <option value="">All statuses</option>
              {STATUSES.map((st) => <option key={st} value={st}>{st} ({s.byStatus[st] || 0})</option>)}
            </select>
            <select aria-label="Filter by ref. party" value={refFilter} onChange={(e) => { setRefFilter(e.target.value); setVisible(PAGE_SIZE); }} style={{ ...inputStyle, width: 'auto' }}>
              <option value="">All ref. parties</option>
              {refParties.map((rp) => <option key={rp} value={rp}>{rp}</option>)}
            </select>
            <select aria-label="Sort by" value={sortKey} onChange={(e) => setSortKey(e.target.value)} style={{ ...inputStyle, width: 'auto' }}>
              {SORTS.map((o) => <option key={o.key} value={o.key}>Sort: {o.label}</option>)}
            </select>
            <button
              type="button"
              onClick={() => exportCsv(data, filtered)}
              style={{ ...inputStyle, width: 'auto', cursor: 'pointer', fontWeight: 600, marginLeft: 'auto' }}
            >
              Export CSV
            </button>
          </div>

          {data.rows.length === 0 ? (
            <p style={{ color: 'var(--color-text-muted)' }}>No customer purchase data yet.</p>
          ) : (
            <div className="table-scroll">
              <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid var(--color-border)', minWidth: 1100 }}>
                <thead>
                  <tr>
                    <th style={thStyle}>No.</th>
                    <th style={{ ...thStyle, minWidth: 200 }}>
                      <div>Customer</div>
                      <CustomerSearchBox
                        value={search}
                        rows={data.rows.filter((r) => (!refFilter || r.refParty === refFilter) && (!statusFilter || r.status === statusFilter))}
                        scoped={!!refFilter}
                        onChange={(v) => { setSearch(v); setPinnedId(null); setVisible(PAGE_SIZE); }}
                        onPick={(r) => {
                          setSearch(r.customerName); setPinnedId(r.customerId); setStatusFilter(''); setRefFilter('');
                          setVisible(PAGE_SIZE); setExpanded((prev) => ({ ...prev, [r.customerId]: true }));
                        }}
                      />
                    </th>
                    <th style={thStyle}>Ref. Party</th>
                    <th style={thStyle}>Status</th>
                    <th style={thStyle}>Last 6 Months Purchases</th>
                    <th style={thStyle}>Next 3 Months Prediction</th>
                    <th style={thStyle}>Last Purchase</th>
                    <th style={thStyle}>Usual Gap</th>
                    <th style={thStyle}>Last 6M Qty</th>
                    <th style={thStyle}>Predicted 3M Qty</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 && (
                    <tr>
                      <td colSpan={10} style={{ ...tdStyle, color: 'var(--color-text-muted)' }}>
                        No customers match these filters.{' '}
                        <button type="button" onClick={() => { setSearch(''); setStatusFilter(''); setRefFilter(''); }} style={{ cursor: 'pointer' }}>Clear</button>
                      </td>
                    </tr>
                  )}
                  {shown.map((r, idx) => {
                    const isOpen = expanded[r.customerId] !== undefined ? !!expanded[r.customerId] : autoOpen;
                    return (
                      <React.Fragment key={r.customerId}>
                        <tr
                          onClick={() => setExpanded((prev) => ({ ...prev, [r.customerId]: !isOpen }))}
                          style={{ cursor: 'pointer', background: isOpen ? 'var(--color-surface-alt)' : (idx % 2 ? 'rgba(148, 163, 184, 0.10)' : 'transparent') }}
                        >
                          <td className="data-figure" style={tdStyle}>{idx + 1}</td>
                          <td style={{ ...tdStyle, fontWeight: 600 }}>
                            {r.customerName}
                            <div style={{ fontWeight: 400, fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                              {[r.city, r.state].filter(Boolean).join(', ')}
                            </div>
                          </td>
                          <td style={tdStyle}>{r.refParty || '—'}</td>
                          <td style={tdStyle}><StatusPill status={r.status} /></td>
                          <td style={{ ...tdStyle, minWidth: 220 }}>
                            {r.history.length === 0 ? <span style={{ color: 'var(--color-text-muted)' }}>No purchase in last 6 months</span> : (
                              <>
                                {r.history.slice(0, 3).map((p) => <PurchaseBlock key={p.date} p={p} color="var(--color-accent)" maxItems={3} />)}
                                {r.history.length > 3 && (
                                  <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                                    +{r.history.length - 3} more purchases (click row)
                                  </div>
                                )}
                              </>
                            )}
                          </td>
                          <td style={{ ...tdStyle, minWidth: 220 }}>
                            {r.predictions.length === 0 ? (
                              <span style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-xs)' }}>{r.note || 'No forecast'}</span>
                            ) : (
                              <>
                                <PurchaseBlock p={r.predictions[0]} color="var(--color-accent-warm, #d97706)" maxItems={3} />
                                {r.qtyRange.low !== r.qtyRange.high && (
                                  <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginBottom: 4 }}>
                                    Usually {r.qtyRange.low} to {r.qtyRange.high} per order
                                  </div>
                                )}
                                {r.predictedDates.length > 1 && (
                                  <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', marginBottom: 4 }}>
                                    Then about every {r.typicalGapDays} days: {r.predictedDates.slice(1, 4).map(fmtShort).join(' · ')}
                                    {r.predictedOrderCount > 4 ? ` +${r.predictedOrderCount - 4} more` : ''}
                                  </div>
                                )}
                                <div style={{ fontSize: 'var(--font-size-xs)', fontWeight: 600, color: CONFIDENCE_COLORS[r.confidence] }}>
                                  Confidence: {r.confidence}{r.confidence === 'Low' ? ' (rough estimate)' : ''}
                                </div>
                                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>{r.confidenceReason}</div>
                              </>
                            )}
                          </td>
                          <td style={tdStyle}>
                            {fmtDate(r.lastPurchaseDate)}
                            <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>{r.daysSinceLast} days ago</div>
                          </td>
                          <td className="data-figure" style={tdStyle}>{r.typicalGapDays ? `${r.typicalGapDays} days` : '—'}</td>
                          <td className="data-figure" style={tdStyle}>
                            {r.qtyLast6m.toLocaleString()}
                            {r.trendPct !== null && (
                              <div style={{ fontSize: 'var(--font-size-xs)', color: r.trendPct >= 0 ? 'var(--color-success)' : 'var(--color-danger)' }}>
                                {r.trendPct >= 0 ? '▲' : '▼'} {Math.abs(r.trendPct)}% vs previous 6M
                              </div>
                            )}
                          </td>
                          <td className="data-figure" style={{ ...tdStyle, fontWeight: 600 }}>{r.predictedQty3m ? r.predictedQty3m.toLocaleString() : '—'}</td>
                        </tr>
                        {isOpen && (
                          <tr style={{ background: 'var(--color-surface-alt)' }}>
                            <td />
                            <td colSpan={9} style={{ padding: '10px 6px 16px', borderBottom: '1px solid var(--color-border)' }}>
                              {r.note && <p style={{ margin: '0 0 8px', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>{r.note}</p>}
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 'var(--space-5)' }}>
                                <div>
                                  <div style={{ fontSize: 'var(--font-size-xs)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>
                                    All purchases, last 6 months ({r.history.length})
                                  </div>
                                  {r.history.length === 0 ? <span style={{ color: 'var(--color-text-muted)' }}>None</span>
                                    : r.history.map((p) => <PurchaseBlock key={p.date} p={p} color="var(--color-accent)" />)}
                                </div>
                                <div>
                                  <div style={{ fontSize: 'var(--font-size-xs)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>
                                    Expected orders, next 3 months ({r.predictedOrderCount})
                                  </div>
                                  {r.predictions.length === 0 ? <span style={{ color: 'var(--color-text-muted)' }}>No forecast</span> : (
                                    <>
                                      <div style={{ fontSize: 'var(--font-size-sm)', lineHeight: 1.7 }}>
                                        {r.predictedDates.map((d) => fmtDate(d)).join(' · ')}
                                        {r.predictedOrderCount > r.predictedDates.length ? ` · +${r.predictedOrderCount - r.predictedDates.length} more` : ''}
                                      </div>
                                      <div style={{ marginTop: 6, fontSize: 'var(--font-size-sm)' }}>
                                        Each order about <strong className="data-figure">{r.typicalQty}</strong> (usually {r.qtyRange.low} to {r.qtyRange.high}).
                                        Total for the 3 months about <strong className="data-figure">{r.predictedQty3m}</strong> ({r.predictedQty3mRange.low} to {r.predictedQty3mRange.high}).
                                      </div>
                                      <div style={{ marginTop: 6, fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>Typical size / design mix per order:</div>
                                      <ItemLines items={r.predictions[0].items} />
                                      <div style={{ marginTop: 6, fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', lineHeight: 1.5 }}>
                                        Confidence {r.confidence}: {r.confidenceReason}. The same quantity and mix are assumed for every date, so this is a pattern-based
                                        estimate. Real orders will vary around the usual range.
                                      </div>
                                    </>
                                  )}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {filtered.length > visible && (
            <div style={{ textAlign: 'center', marginTop: 'var(--space-3)' }}>
              <button type="button" onClick={() => setVisible((v) => v + PAGE_SIZE)} style={{ ...inputStyle, width: 'auto', cursor: 'pointer', fontWeight: 600 }}>
                Show {Math.min(PAGE_SIZE, filtered.length - visible)} more ({filtered.length - visible} remaining)
              </button>
            </div>
          )}

          <p style={{ marginTop: 'var(--space-4)', marginBottom: 0, fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', lineHeight: 1.5 }}>
            As of {fmtDate(data.asOf)}. The forecast is statistical, not a promise: next date = last purchase + the customer&apos;s usual gap
            (average of past gaps); 3-month qty = their own run-rate (last 6 months qty ÷ 2); size/design = their top combinations over the last 12 months.
            Seasonal effects are not included. Customers far overdue are marked &quot;At risk&quot; and are not forecast.
          </p>
        </>
      )}
    </Card>
  );
}
